import history from 'service/customHistory';
import classNames from 'classnames';
import BootstrapNav from 'react-bootstrap/Nav';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import ELEMENT from 'common/element';

import Tab from 'components/common/Tab/Tab';
import Badge from 'components/common/Badge/Badge';

import './TabbedPageHeader.scss';

type TabConfig = {
  title:    string;
  eventKey: string;
  badge?:   number;
  testId?:  ELEMENT;
};

type TabbedPageHeaderProps = {
  title?:         string;
  tabs?:           Array<TabConfig>;
  headerItems?:   Array<JSX.Element>;
  className?:     string;
  hasBackButton?: boolean;
};
/**
 * Tabbed page header is a component that renders a page header with title, tabs, and additional
 * elements
 * on the right side of the tabs. The underline of the tabs will either fill the whole header, or
 * fill until the items or help button.
 *
 * tabs - a list of json and json should have this format {title: <string>, eventKey: <string>}
 *
 * @prop title            The tabbed page title.
 * @prop tabs             A list of tab json.
 * @prop headerItems      Additional items on the tab header and is a list of fragments.
 * @prop className        Additional class(es).
 * @prop hasBackButton    A boolean that renders a back button if true
 */
function TabbedPageHeader({
  title,
  tabs = [],
  headerItems,
  className,
  hasBackButton = false,
}: TabbedPageHeaderProps): JSX.Element {

  const upperLeftTitle = title ? (
    <div className="TabbedPageHeader__Item">
      <span
        className="TabbedPageHeader__Title"
        data-test-id={ELEMENT.commonTabbedPageHeaderTitle}
      >
        {title}
      </span>
    </div>
  ) : null;

  const shouldRenderTabs = tabs.length > 1;

  const renderTab = (tab: TabConfig): JSX.Element => (
    <Tab eventKey={tab.eventKey} key={tab.eventKey} testId={tab?.testId}>
      {tab.title}
      {tab.badge != null ? <Badge variant="info">{tab.badge}</Badge> : null}
    </Tab>
  );

  const renderTabs = shouldRenderTabs ? tabs.map(renderTab) : null;

  const headerTabs = (
    <BootstrapNav className="TabbedPageHeader__Tabs">
      {renderTabs}
    </BootstrapNav>
  );

  const items = headerItems ? (
    <div className="TabbedPageHeader__Item">
      {headerItems}
    </div>
  ) : null;

  const backButton = hasBackButton && (
    <button
      className="TabbedPageHeader__BackButton"
      onClick={history?.back}
      type="button"
    >
      <ArrowBackIosIcon />
    </button>
  );

  return (
    <div className={classNames('TabbedPageHeader', className)}>
      {backButton}
      {upperLeftTitle}
      {headerTabs}
      {items}
    </div>
  );
}

export default TabbedPageHeader;
